import { useContext, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { TranslationContext } from '../../../assets/contexts/translationContext';
import { CheckIcon, CrossIcon } from '../../../assets/icons/icons';
import { requestHistoryExport } from '../../../assets/services/historyExport';
import BottomSheet from '../../shared/BottomSheet/BottomSheet';
import './ExportHistory.css';

function ExportHistory() {
  const navigate = useNavigate();
  const location = useLocation();
  const { common, history } = useContext(TranslationContext);
  const [dateRange, setDateRange] = useState('all-time');
  const [isExporting, setIsExporting] = useState(false);
  const dateRanges = useMemo(() => [
    { id: 'all-time', label: history.allTime },
    { id: 'this-month', label: history.thisMonth },
    { id: 'last-90-days', label: history.last90Days },
    { id: 'custom', label: history.customRange },
  ], [history]);

  const handleClose = () => {
    if (location.state?.backgroundLocation) {
      navigate(-1);
      return;
    }

    navigate('/history', { replace: true });
  };

  const handleDownload = async () => {
    if (isExporting) {
      return;
    }

    setIsExporting(true);

    try {
      const exportFile = await requestHistoryExport({ dateRange });

      navigate('/history/export/ready', {
        replace: true,
        state: {
          ...(location.state?.backgroundLocation
            ? { backgroundLocation: location.state.backgroundLocation }
            : {}),
          exportFile,
          skipRouteOverlayTransition: true,
        },
      });
    } catch (error) {
      toast.error(error?.message || history.exportFailed);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <BottomSheet
      className="export-history"
      panelClassName="export-history__panel"
      titleId="export-history-title"
      onClose={handleClose}
    >
      <header className="export-history__header">
        <h2 id="export-history-title" className="export-history__title">
          {history.exportTitle}
        </h2>
        <button
          type="button"
          className="export-history__close"
          aria-label={common.close}
          onClick={handleClose}
        >
          <CrossIcon className="export-history__close-icon" />
        </button>
      </header>

      <p className="export-history__section-label">{history.exportDateRange}</p>
      <div className="export-history__card">
        {dateRanges.map((option) => (
          <button
            key={option.id}
            type="button"
            className="export-history__option"
            onClick={() => setDateRange(option.id)}
          >
            <span>{option.label}</span>
            <span
              className={
                dateRange === option.id
                  ? 'export-history__radio export-history__radio--checked'
                  : 'export-history__radio'
              }
              aria-hidden="true"
            >
              {dateRange === option.id && <CheckIcon className="export-history__radio-icon" />}
            </span>
          </button>
        ))}
      </div>

      <p className="export-history__note">
        {history.exportNote}
      </p>

      <button
        type="button"
        className="export-history__download"
        onClick={handleDownload}
        disabled={isExporting}
      >
        {isExporting ? history.exportPreparing : history.exportCsv}
      </button>
    </BottomSheet>
  );
}

export default ExportHistory;
